import { useState } from "react";
import {
    Button,
    Popover,
    PopoverHandler,
    PopoverContent,
    Typography,
} from "@material-tailwind/react";
import {
    DocumentCheckIcon,
    DocumentTextIcon,
    EyeIcon,
    CheckCircleIcon,
    XCircleIcon,
} from "@heroicons/react/24/solid";
import moment from "moment";
import DocumentViewModal from "@/components/DocumentViewModal";
import Swal from "sweetalert2";
import Toaster from "@/components/Toaster";
import { approveRejectPaymentReceipt, getRfqById, initiatePaymentByAdmin } from "@/store/slices/rfqSlice";
import { useDispatch, useSelector } from "react-redux";
import DownloadFile from "@/components/DownloadFile";
import { useTranslation } from "react-i18next";

export default function RfqPaymentReceiptPopover({ rfq_id, receipts = [], payment_summary = null, rfq_details, buttonText = "Payment Receipts" }) {
    const dispatch = useDispatch();
    const { t } = useTranslation('rfq');
    const { t: ct } = useTranslation("common");

    const [open, setOpen] = useState(false);
    const [viewFile, setViewFile] = useState(null);

    const { user } = useSelector((state) => state.user);

    const onApproveReject = (type, id) => {
        const msg = type === "approved" ? "approve" : "reject";
        
        if (type === 'approved') {
            Swal.fire({
                title: t("approve_receipt_title"),
                icon: "question",
                html: `
                    <div class="text-left mt-4">
                        <div class="mb-4">
                            <label for="swal-amount" class="block text-sm font-medium text-gray-700 mb-1">
                                ${t("receipt_amount_label")}
                            </label>
                            <input 
                                type="number" 
                                id="swal-amount" 
                                class="swal2-input !m-0 !w-full !border !border-gray-300 focus:!border-green-500 !rounded-md !shadow-sm" 
                                placeholder="0.00" 
                                step="0.01"
                            >
                        </div>
                        
                        <div class="mb-2">
                            <label for="swal-payment-type" class="block text-sm font-medium text-gray-700 mb-1">
                                ${t("payment_type_label")}
                            </label>
                            <select 
                                id="swal-payment-type" 
                                class="swal2-select !m-0 !w-full !flex !border !border-gray-300 focus:!border-green-500 !rounded-md !shadow-sm !h-[42px] !px-2"
                            >
                                <option value="" disabled selected>${t("select_payment_type_placeholder")}</option>
                                <option value="1">${t("payment_type_cash")}</option>
                                <option value="2">${t("payment_type_netbanking")}</option>
                                <option value="3">${t("payment_type_upi")}</option>
                                <option value="4">${t("payment_type_rtgs")}</option>
                                <option value="5">${t("payment_type_cheque")}</option>
                                <option value="6">${t("payment_type_others")}</option>
                            </select>
                        </div>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: t("approve_and_save_button"),
                cancelButtonText: ct("cancel"),
                confirmButtonColor: "#16a34a",
                cancelButtonColor: "#d33",
                focusConfirm: false,
                preConfirm: () => {
                    const amount = document.getElementById('swal-amount').value;
                    const paymentType = document.getElementById('swal-payment-type').value;

                    if (!amount) {
                        Swal.showValidationMessage(t("receipt_amount_required"));
                        return false;
                    }
                    if (Number(amount) <= 0) {
                        Swal.showValidationMessage(t("amount_greater_than_zero"));
                        return false;
                    }
                    if (!paymentType) {
                        Swal.showValidationMessage(t("payment_type_required"));
                        return false;
                    }

                    return { 
                        amount_paid: Number(amount), 
                        payment_mode: paymentType 
                    };
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    const { amount_paid, payment_mode } = result.value;
                    
                    dispatch(approveRejectPaymentReceipt({ 
                        status: type, 
                        id: id, 
                        amount_paid, 
                        payment_mode 
                    }))
                    .unwrap()
                    .then(() => {
                        Toaster.success(t("receipt_approved_success"));
                        dispatch(getRfqById(rfq_id));
                    })
                    .catch(() => Toaster.error(ct("actionFailed")));
                }
            });

        } else {
            Swal.fire({
                title: t("reject_receipt_title"),
                text: t("reject_receipt_text"),
                icon: "warning",
                input: "textarea", // Using textarea for longer reasons
                inputPlaceholder: t("enter_rejection_reason_placeholder"),
                inputAttributes: {
                    autocapitalize: "off"
                },
                showCancelButton: true,
                confirmButtonColor: "#16a34a",
                cancelButtonColor: "#dc2626",
                confirmButtonText: t("submit_rejection_button"),
                // --- Validation Logic ---
                inputValidator: (value) => {
                    if (!value) {
                        return t("reason_required_for_rejection");
                    }
                    if (value.length < 10) {
                        return t("reason_min_chars");
                    }
                    if (value.length > 100) {
                        return t("reason_max_chars");
                    }
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    // result.value contains the text entered by the user
                    const reason = result.value;

                    dispatch(approveRejectPaymentReceipt({ 
                        status: type, 
                        id: id, 
                        reason: reason // Pass the reason to your API
                    }))
                    .unwrap()
                    .then(() => {
                        Toaster.success(t("receipt_rejected_success"));
                        dispatch(getRfqById(rfq_id));
                    })
                    .catch(() => Toaster.error(t("failed_to_reject")));
                }
            });
        }
    };

    const initiatePayment = () => {
        Swal.fire({
            title: t("initiate_payment_title"),
            icon: "question",
            html: `
                <div class="text-left mt-4">

                    <!-- Amount -->
                    <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            ${t("amount_label_simple")}
                        </label>
                        <input 
                            type="number" 
                            id="swal-amount" 
                            class="swal2-input !m-0 !w-full !border !border-gray-300 focus:!border-green-500 !rounded-md !shadow-sm" 
                            placeholder="0.00" 
                            step="0.01"
                        >
                    </div>

                    <!-- Payment Type -->
                    <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            ${t("payment_type_label")}
                        </label>
                        <select 
                            id="swal-payment-type" 
                            class="swal2-select !m-0 !w-full !border !border-gray-300 focus:!border-green-500 !rounded-md !shadow-sm !h-[42px] !px-2"
                        >
                            <option value="" disabled selected>${t("select_payment_type_placeholder")}</option>
                            <option value="1">${t("payment_type_cash")}</option>
                            <option value="2">${t("payment_type_netbanking")}</option>
                            <option value="3">${t("payment_type_upi")}</option>
                            <option value="4">${t("payment_type_rtgs")}</option>
                            <option value="5">${t("payment_type_cheque")}</option>
                            <option value="6">${t("payment_type_others")}</option>
                        </select>
                    </div>

                    <!-- File Upload -->
                    <div class="mb-2">
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            ${t("upload_receipt_label")}
                        </label>
                        <input 
                            type="file" 
                            id="swal-file" 
                            accept=".pdf,image/*"
                            class="swal2-file !m-0 !w-full"
                        >
                        <p class="text-xs text-gray-500 mt-1">
                            ${t("allowed_file_types_receipt")}
                        </p>
                    </div>

                </div>
            `,
            showCancelButton: true,
            confirmButtonText: t("submit_payment_button"),
            cancelButtonText: ct("cancel"),
            confirmButtonColor: "#16a34a",
            cancelButtonColor: "#d33",
            focusConfirm: false,

            preConfirm: () => {
                const amount = document.getElementById("swal-amount").value;
                const paymentType = document.getElementById("swal-payment-type").value;
                const fileInput = document.getElementById("swal-file");
                const file = fileInput.files[0];

                // Amount validation
                if (!amount) {
                    Swal.showValidationMessage(t("amount_required"));
                    return false;
                }
                if (Number(amount) <= 0) {
                    Swal.showValidationMessage(t("amount_greater_than_zero"));
                    return false;
                }

                if (Number(amount) >= Number(payment_summary?.due_amount)) {
                    Swal.showValidationMessage(t("amount_exceed_pending_error", { amount: payment_summary?.due_amount }));
                    return false;
                }

                // Payment type validation
                if (!paymentType) {
                    Swal.showValidationMessage(t("payment_type_required"));
                    return false;
                }

                // File validation
                if (!file) {
                    Swal.showValidationMessage(t("payment_receipt_required"));
                    return false;
                }

                const allowedTypes = [
                    "application/pdf",
                    "image/jpeg",
                    "image/png",
                    "image/jpg"
                ];

                if (!allowedTypes.includes(file.type)) {
                    Swal.showValidationMessage(t("payment_file_type_error"));
                    return false;
                }

                return {
                    amount_paid: Number(amount),
                    payment_mode: paymentType,
                    payment_receipt: file
                };
            }

        }).then((result) => {
            if (result.isConfirmed) {
                const { amount_paid, payment_mode, payment_receipt } = result.value;

                // IMPORTANT: use FormData for file upload
                const formData = new FormData();
                formData.append("amount_paid", amount_paid);
                formData.append("payment_mode", payment_mode);
                formData.append("payment_receipt[]", payment_receipt);

                dispatch(initiatePaymentByAdmin({rfq_id, formData}))
                    .unwrap()
                    .then((res) => {
                        if(res?.status){
                            Toaster.success(t("payment_initiated_success"));
                            dispatch(getRfqById(rfq_id));
                        }else{
                            Toaster.error(res?.message || ct("somethingWentWrong"))
                        }
                    })
                    .catch(() => Toaster.error(ct("actionFailed")));
            }
        });
    };
    
    
    const formatStatus = (status = "") =>
        status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    const statusColor = (status) => {
        switch (status) {
            case "approved":
                return "bg-green-100 text-green-700";
            case "rejected":
                return "bg-red-100 text-red-700";
            case "pending":
                return "bg-yellow-100 text-yellow-700";
            default:
                return "bg-gray-100 text-gray-600";
        }
    };
    const isFinanceOrAdmin =
        user?.roles?.includes("Finance") ||
        user?.roles?.includes("Administrator");


    return (
        <>
            <Popover open={open} handler={setOpen} placement="bottom-start">
                <PopoverHandler>
                    <div className="flex items-center gap-2">
                        <button className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all shadow-sm bg-gray-200 text-gray-700 hover:bg-gray-300 hover:text-gray-900">
                            <DocumentCheckIcon className="h-4 w-4" /> {buttonText} ({receipts.length})
                        </button>
                    </div>
                </PopoverHandler>
                <PopoverContent className="w-[520px] p-0 rounded-2xl shadow-xl border border-gray-200">
                    {/* Header */}
                   <div className="p-4 border-b bg-gray-50">
                    <div className="flex items-start justify-between gap-3">
                    {/* Left Content */}
                    <div className="flex flex-col">
                        <Typography variant="h6" className="font-semibold text-gray-900 leading-tight">
                        {t("payment_receipts_title")}
                        </Typography>
                        <Typography className="text-xs text-gray-500 mt-0.5">
                        {t("payment_receipts_desc")}
                        </Typography>
                        {(receipts?.length == 0 && payment_summary?.credit_date) && (() => {
                            const creditDate = payment_summary?.credit_date;

                            if (!creditDate) return null;

                            const now = moment();
                            const dueDate = moment(creditDate);

                            let status = "";
                            let colorClass = "";

                            if (now.isAfter(dueDate)) {
                                status = t("credit_date_status_delayed");
                                colorClass = "text-red-500";
                            } else if (now.isSame(dueDate, "day")) {
                                status = t("credit_date_status_due");
                                colorClass = "text-yellow-500";
                            } else {
                                status = t("credit_date_status_on_time");
                                colorClass = "text-green-600";
                            }

                            return (
                                <p className={`text-sm font-medium ${colorClass}`}>
                                    {t("credit_date_label")}: {dueDate.format("D MMM YYYY")} | {status}
                                </p>
                            );
                        })()}
                        {(receipts?.length > 0 && payment_summary?.credit_date) && 
                            <p className={`text-sm font-medium`}>
                                {t("credit_date_label")}: {moment(payment_summary?.credit_date)?.format("D MMM YYYY")}
                            </p>
                        }
                    </div>

                    {/* Right Side: Status + Button */}
                    <div className="flex items-center gap-2">

                        {/* Status Badge */}
                        {(rfq_details?.customer_approval === 'yes' && payment_summary?.status) && (
                        <span
                            className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full capitalize
                            ${
                            payment_summary?.status === "paid"
                                ? "bg-green-100 text-green-700"
                                : payment_summary?.status === "partial"
                                ? "bg-amber-100 text-amber-700"
                                : payment_summary?.status === "pending"
                                ? "bg-red-100 text-red-600"
                                : "bg-gray-100 text-gray-600"
                            }`}
                        >
                            {payment_summary?.status.replace(/_/g, " ")}
                        </span>
                        )}

                        {/* Initiate Payment Button */}
                        {(payment_summary?.status !== "paid" && user?.roles[0] === 'Finance')&& <Button
                        onClick={() => {
                            initiatePayment();
                        }}
                        className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material">
                        
                        Initiate Payment
                        </Button>}

                    </div>

                    </div>
                    </div>
                    {rfq_details?.customer_approval === 'yes' && 
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                            <div className="grid grid-cols-3 gap-4">

                                {/* Total Amount */}
                                <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                                <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide">
                                    {t("total_amount_receipt")}
                                </p>
                                <p className="text-sm font-bold text-gray-900 mt-1">
                                    SAR {payment_summary?.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "0.00"}
                                </p>
                                </div>

                                {/* Amount Paid */}
                                <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                                <p className="text-[10px] text-green-600 uppercase font-semibold tracking-wide">
                                    {t("amount_paid_receipt")}
                                </p>
                                <p className="text-sm font-bold text-green-700 mt-1">
                                    SAR {payment_summary?.paid_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "0.00"}
                                </p>
                                </div>

                                {/* Amount Pending */}
                                <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                                <p className="text-[10px] text-orange-500 uppercase font-semibold tracking-wide">
                                    {t("amount_pending_receipt")}
                                </p>
                                <p className="text-sm font-bold text-orange-700 mt-1">
                                    SAR {payment_summary?.due_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "0.00"}
                                </p>
                                </div>


                            </div>
                        </div>
                    }

                    {/* List */}
                    {receipts.length ? (
                        receipts.map((receipt, index) => {
                            const isPending = receipt.status === "pending";
                            const isRejected = receipt.status === "rejected";
                            const isApproved = receipt.status === "approved";

                            return (
                                <div
                                    key={receipt.id}
                                    className="p-3 border bg-white hover:border-blue-200 transition"
                                >
                                    {/* Top */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex gap-3">
                                            <div className="p-2 rounded-lg bg-blue-50">
                                                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold">
                                                    {t("receipt_number", { index: index + 1 })}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {moment(receipt.created_at).format(
                                                        "MMM DD, YYYY • hh:mm A"
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <span
                                            className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColor(
                                                receipt.status
                                            )}`}
                                        >
                                            {formatStatus(receipt.status)}
                                        </span>
                                    </div>
                                  
                                    {/* Actions */}
                                    <div className="mt-3 flex items-center justify-between">
                                        <button
                                            onClick={() =>
                                                setViewFile(receipt.payment_receipt?.[0])
                                            }
                                            className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                        >
                                            <EyeIcon className="h-4 w-4" />
                                            {t("view_receipt_button")}
                                        </button>

                                        {isApproved && (
                                            <DownloadFile fileUrl={receipt.payment_invoice} />
                                        )}

                                        {isFinanceOrAdmin && !isApproved && (
                                            <div className="flex gap-2">
                                                {isPending && (
                                                    <Button
                                                        variant="outlined"
                                                        color="red"
                                                        size="sm"
                                                        onClick={() =>
                                                            onApproveReject("rejected", receipt.id)
                                                        }
                                                    >
                                                        {ct("reject")}
                                                    </Button>
                                                )}

                                                {(isPending || isRejected) && (
                                                    <Button
                                                        variant="gradient"
                                                        color="green"
                                                        size="sm"
                                                        onClick={() =>
                                                            onApproveReject("approved", receipt.id)
                                                        }
                                                    >
                                                        {ct("approve")}
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {receipt?.rejected_reason && <div className="mt-3 flex items-center justify-between">
                                        <p className="text-xs text-gray-500">
                                            {receipt?.rejected_reason || ''}
                                        </p>
                                    </div>}
                                </div>
                            );
                        })
                    ) : (
                        <p className="text-center text-sm text-gray-500 italic">
                            {t("no_receipts_uploaded")}
                        </p>
                    )}

                </PopoverContent>
            </Popover>

            {viewFile && (
                <DocumentViewModal
                    documentUrl={viewFile}
                    setIsDocumentViewModalOpen={() => setViewFile(null)}
                />
            )}
        </>
    );
}
